// 거래명세서 엑셀 내려받기. 인쇄 미리보기와 같은 표시 모델(InvoiceDocumentDto)에서 만든다.
//
// 인쇄본과 컬럼 구성·소계 캡션·소계행 공란 규칙을 그대로 따르되, 엑셀은 장 개념이 없어
// invoice-paginate의 46행 나누기는 적용하지 않는다(문서 1건 = 시트 1장, 그룹은 이어서 쓴다).
// 금액·수량은 문자열이 아니라 숫자 셀로 써서 받는 쪽이 바로 집계할 수 있게 한다.
//
// 표 서식(테두리·제목·헤더 강조)이 필요해 exceljs를 쓴다 — 같이 설치돼 있는 SheetJS
// 무료판(xlsx)은 셀 스타일 쓰기를 지원하지 않는다. 번들이 커서 동적 import로 분리한다.

import type ExcelJSNS from 'exceljs';
import { SUBTOTAL_CAPTION } from './invoice-copy';
import type { InvoiceDocumentDto, InvoiceParty } from './invoice-document-types';

const COLUMNS = ['월일', '품목명', '단위', '차량NO', '수량', '단가', '금액', '공급가액', '세액'];
const COL_WIDTHS = [8, 22, 6, 12, 12, 12, 14, 14, 12];
const AMT_FMT = '#,##0';
/** 수량 칸만 소수를 남긴다 — 유형 6의 소계는 정수가 아니다 */
const QTY_FMT = '#,##0.##';

const THIN = { style: 'thin' as const };
const BORDER = { top: THIN, left: THIN, right: THIN, bottom: THIN };
const fill = (argb: string): ExcelJSNS.Fill => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
const HEADER_FILL = fill('FFE8E8E8');
const SUBTOTAL_FILL = fill('FFF5F5F5');

type CellStyle = Partial<Pick<ExcelJSNS.Cell, 'border' | 'fill' | 'font' | 'alignment'>>;

/** 서식은 A~I 셀에만 준다. row.border/fill은 행 서식으로 저장돼 Excel이 J열 이후 빈 칸까지 칠한다 */
function styleCells(row: ExcelJSNS.Row, style: CellStyle) {
  for (let i = 1; i <= COLUMNS.length; i++) Object.assign(row.getCell(i), style);
}

const TH: CellStyle = { fill: HEADER_FILL, font: { bold: true }, alignment: { horizontal: 'center' } };

const PARTY_ROWS: [string, keyof InvoiceParty][] = [
  ['등록NO', 'bizNo'],
  ['대표', 'ceo'],
  ['상 호', 'name'],
  ['소재지', 'address'],
];

/** 인쇄본처럼 공급자(A~E)·공급받는자(F~I)를 좌우로 놓는다. 9열이라 등록NO와 대표는 한 줄에 못 넣고 행을 나눈다 */
function addPartyBlocks(sheet: ExcelJSNS.Worksheet, supplier: InvoiceParty, customer: InvoiceParty) {
  const value = (party: InvoiceParty, key: keyof InvoiceParty) =>
    key === 'name' && party.code ? `${party.code} ${party.name}` : (party[key] ?? '');
  const rows = PARTY_ROWS.map(([th, key]) =>
    sheet.addRow(['공급자', th, value(supplier, key), '', '', '공급받는자', th, value(customer, key)])
  );
  const top = rows[0].number;
  const bottom = top + PARTY_ROWS.length - 1;
  for (const row of rows) {
    styleCells(row, { border: BORDER, alignment: { vertical: 'middle', shrinkToFit: true } });
    Object.assign(row.getCell(2), TH);
    Object.assign(row.getCell(7), TH);
    sheet.mergeCells(row.number, 3, row.number, 5);
    sheet.mergeCells(row.number, 8, row.number, COLUMNS.length);
  }
  const label: CellStyle = { ...TH, alignment: { horizontal: 'center', vertical: 'middle', wrapText: true } };
  Object.assign(rows[0].getCell(1), label);
  Object.assign(rows[0].getCell(6), label);
  sheet.mergeCells(top, 1, bottom, 1);
  sheet.mergeCells(top, 6, bottom, 6);
}

function buildSheet(sheet: ExcelJSNS.Worksheet, doc: InvoiceDocumentDto) {
  // 열 너비 합(112자)이 A4 세로 인쇄 폭을 넘어 폭 맞춤으로 축소 인쇄한다(세로는 장 수 제한 없음)
  sheet.pageSetup = { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
  // 5열(수량)·6~9열(금액)은 컬럼 서식으로 지정 — 텍스트 셀에는 영향이 없다
  sheet.columns = COL_WIDTHS.map((width, i) => ({
    width,
    style: i === 4 ? { numFmt: QTY_FMT } : i >= 5 ? { numFmt: AMT_FMT } : {},
  }));

  const title = sheet.addRow([doc.header.titleText]);
  sheet.mergeCells(1, 1, 1, COLUMNS.length);
  title.getCell(1).font = { bold: true, size: 14 };
  title.getCell(1).alignment = { horizontal: 'center' };
  title.height = 24;

  addPartyBlocks(sheet, doc.header.supplier, doc.header.customer);

  for (const group of doc.groups) {
    sheet.addRow([]);
    const tableTitle = sheet.addRow([group.tableTitle]);
    sheet.mergeCells(tableTitle.number, 1, tableTitle.number, COLUMNS.length);
    tableTitle.getCell(1).font = { bold: true };
    tableTitle.getCell(1).alignment = { horizontal: 'center' };
    styleCells(sheet.addRow(COLUMNS), { border: BORDER, ...TH });

    for (const rowData of group.rows) {
      const isDetail = rowData.rowType === 'detail';
      const row = sheet.addRow([
        isDetail ? rowData.dateText : '',
        rowData.rowType === 'detail' ? rowData.itemName : SUBTOTAL_CAPTION[rowData.rowType],
        isDetail ? rowData.unit : '',
        isDetail ? rowData.carNo : '',
        rowData.qty,
        isDetail ? (rowData.unitPrice ?? '') : '',
        rowData.totalAmt,
        rowData.supplyAmt,
        rowData.vatAmt,
      ]);
      styleCells(row, { border: BORDER });
      if (!isDetail) styleCells(row, { fill: SUBTOTAL_FILL, font: { bold: true } });
    }
  }
}

/** 엑셀 시트명 제약: 31자 이하, []:*?/\ 불가, 중복(확장코드 동명 문서) 불가 */
function sheetName(doc: InvoiceDocumentDto, used: Set<string>): string {
  const base =
    (doc.header.customer.name || doc.docNo).replace(/[[\]:*?/\\]/g, ' ').slice(0, 28) ||
    doc.docNo;
  let name = base;
  for (let i = 2; used.has(name); i++) {
    const suffix = `(${i})`;
    name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
  }
  used.add(name);
  return name;
}

/** 미리보기에 담긴 거래명세서를 엑셀 파일로 내려받는다 */
export async function downloadInvoiceWorkbook(docs: InvoiceDocumentDto[]): Promise<void> {
  const { default: ExcelJS } = await import('exceljs');
  const book = new ExcelJS.Workbook();
  const used = new Set<string>();
  for (const doc of docs) {
    buildSheet(book.addWorksheet(sheetName(doc, used)), doc);
  }

  const buffer = await book.xlsx.writeBuffer();
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  );
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `거래명세서_${docs[0].header.issueYm}.xlsx`;
  anchor.click();
  // click 직후 해제하면 내려받기가 끊길 수 있어 다음 태스크로 미룬다
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
